/**
 * Mixed Fuels Spec Taxonomy
 *
 * Conditional spec dropdowns shown on the variant form when the parent
 * stock item's subtype is `mixed_fuels` (under the Oil/Gas category).
 *
 * Source list provided by product owner with two corrections applied:
 *  - "Marine Diesel Oil (MSO)" -> "Marine Diesel Oil (MDO)" (standard ISO 8217 abbreviation).
 *  - Bare "RMG" entry dropped (not a real ISO grade; viscosity numbers are required).
 *
 * Each FUEL_CATEGORIES entry also carries a `shortcode` used by the
 * shorthand generator (e.g. "VLSFO ISO 8217:2010 RMG 180 0.50%S").
 */

export const MIXED_FUELS_SUBTYPE = 'mixed_fuels';

export const FUEL_CATEGORIES = [
    { value: 'mgo',        label: 'Marine Gas Oil (MGO)',     shortcode: 'MGO' },
    { value: 'ulsfo',      label: 'ULSFO',                    shortcode: 'ULSFO' },
    { value: 'vlsfo',      label: 'VLSFO',                    shortcode: 'VLSFO' },
    { value: 'hsfo',       label: 'HSFO',                     shortcode: 'HSFO' },
    { value: 'mdo',        label: 'Marine Diesel Oil (MDO)',  shortcode: 'MDO' },
    { value: 'biofuels',   label: 'Bio Fuels',                shortcode: 'Bio Fuels' },
    { value: 'lubricants', label: 'Lubricants',               shortcode: 'Lubricants' },
];

export const ISO_GRADES = [
    { value: 'rmg_180', label: 'RMG 180' },
    { value: 'rmg_380', label: 'RMG 380' },
    { value: 'rmg_500', label: 'RMG 500' },
    { value: 'rmg_700', label: 'RMG 700' },
    { value: 'dma',     label: 'DMA' },
    { value: 'dmb',     label: 'DMB' },
    { value: 'rmd_80',  label: 'RMD 80' },
];

export const SULFUR_LIMITS = [
    { value: 'max_0_10', label: 'Max. 0.10%' },
    { value: 'max_0_50', label: 'Max. 0.50%' },
    { value: 'max_3_50', label: 'Max. 3.50%' },
];

export const ISO_8217_YEARS = [
    { value: '2010', label: '2010' },
    { value: '2012', label: '2012' },
    { value: '2017', label: '2017' },
    { value: '2024', label: '2024' },
];

export const MIXED_FUELS_FIELDS = [
    { key: 'fuel_category', label: 'Fuel Category', options: FUEL_CATEGORIES, placeholder: 'Select fuel category...' },
    { key: 'iso_grade',     label: 'ISO Grade',     options: ISO_GRADES,      placeholder: 'Select ISO grade...' },
    { key: 'sulfur_limit',  label: 'Sulfur Limit',  options: SULFUR_LIMITS,   placeholder: 'Select sulfur limit...' },
    { key: 'iso_year',      label: 'ISO 8217 Year', options: ISO_8217_YEARS,  placeholder: 'Select ISO 8217 year...' },
];

export const EMPTY_MIXED_FUELS_SPEC = {
    fuel_category: '',
    iso_grade: '',
    sulfur_limit: '',
    iso_year: '',
};

const FIELD_LOOKUP = MIXED_FUELS_FIELDS.reduce((acc, f) => {
    acc[f.key] = f.options;
    return acc;
}, {});

/**
 * Resolve a stored slug back to its display label for a given mixed-fuels field.
 * Returns the original value when no match is found so we never lose data.
 */
export const getMixedFuelsLabel = (fieldKey, value) => {
    if (!value) return '';
    const options = FIELD_LOOKUP[fieldKey];
    if (!options) return value;
    const match = options.find((opt) => opt.value === value);
    return match ? match.label : value;
};

export const isMixedFuelsSubtype = (subtype) => subtype === MIXED_FUELS_SUBTYPE;

/**
 * True when at least one of the four spec keys carries a non-empty value.
 */
export const hasMixedFuelsSpec = (specAttributes) => {
    if (!specAttributes || typeof specAttributes !== 'object') return false;
    return MIXED_FUELS_FIELDS.some((f) => !!specAttributes[f.key]);
};

/**
 * Categories for which the sulfur slot is replaced with "(other)" because the
 * ISO 8217 sulfur ladder doesn't apply to them in the same way.
 */
const OTHER_SULFUR_CATEGORIES = new Set(['biofuels', 'lubricants']);

/**
 * Convert a stored sulfur slug into its compact display form, e.g.
 *   "max_0_50" -> "0.50%S"
 * Falls back to the label without the "Max. " prefix if the slug is unknown.
 */
const formatSulfur = (sulfurSlug) => {
    if (!sulfurSlug) return '';
    const match = sulfurSlug.match(/^max_(\d+)_(\d+)$/);
    if (match) {
        return `${match[1]}.${match[2]}%S`;
    }
    const label = getMixedFuelsLabel('sulfur_limit', sulfurSlug);
    return label.replace(/^Max\.\s*/, '').replace(/%$/, '%S');
};

/**
 * Build the canonical mixed-fuels shorthand string, e.g.
 *   "VLSFO ISO 8217:2010 RMG 180 0.50%S"
 *   "Lubricants ISO 8217:2024 DMB (other)"
 *
 * Returns '' if any of the four fields are missing so partially-filled
 * specs don't render a misleading shorthand.
 */
export const getMixedFuelsShorthand = (specAttributes) => {
    if (!hasMixedFuelsSpec(specAttributes)) return '';
    const { fuel_category, iso_grade, sulfur_limit, iso_year } = specAttributes;
    if (!fuel_category || !iso_grade || !iso_year) return '';

    const categoryEntry = FUEL_CATEGORIES.find((c) => c.value === fuel_category);
    const shortcode = categoryEntry ? categoryEntry.shortcode : fuel_category;
    const gradeLabel = getMixedFuelsLabel('iso_grade', iso_grade);

    const sulfurDisplay = OTHER_SULFUR_CATEGORIES.has(fuel_category)
        ? '(other)'
        : (sulfur_limit ? formatSulfur(sulfur_limit) : '');

    if (!sulfurDisplay) return '';

    return `${shortcode} ISO 8217:${iso_year} ${gradeLabel} ${sulfurDisplay}`;
};

// ---------------------------------------------------------------------------
// Sprint 7A: Stock-picker integration helpers (Fuels surfaces)
// ---------------------------------------------------------------------------
// The picker reuses the existing ``RecapProductSearch`` widget. Sprint 7
// L4: filter to ``category='oil_gas' AND product_type='mixed_fuels'`` so
// the trader can't pick a coal variant on a fuels deal. Other oil_gas
// subtypes (diesel, jet_fuel, LNG, ...) are out of scope for the Fuels
// picker and join in a later sprint when needed.

/**
 * Predicate that decides whether a Stock variant is pickable on a fuels
 * deal surface. Passes any ``oil_gas`` variant (mixed_fuels, diesel,
 * gasoline, natural_gas, lng, jet_fuel) so the fuels picker covers the
 * full Oil & Gas catalogue while excluding coal / ores / cement /
 * other solid commodities. Pass this as the ``filterFunction`` prop to
 * ``RecapProductSearch``.
 *
 * NOTE: The four-dropdown ``spec_attributes`` autofill (fuel_category,
 * iso_grade, sulfur_limit, iso_year) only fires for ``mixed_fuels``
 * variants -- other oil_gas subtypes carry no ``spec_attributes`` and
 * gracefully leave the deal-side spec dropdowns blank for the trader
 * to fill in manually.
 */
export const FUELS_VARIANT_FILTER = (variant) => {
    if (!variant) return false;
    return variant.category === 'oil_gas';
};

/**
 * Loose, case-insensitive name match for a fuels-spec lookup row.
 * Handles small typographical drift between the variant's slug-derived
 * label and the lookup table's stored ``name`` -- e.g. "RMG_380" vs
 * "RMG 380", or "Max. 0.50%" vs "Max 0.50%".
 *
 * Returns the matching row, or ``null`` when no candidate is close
 * enough.
 */
const _findLookupByCandidates = (rows, candidates) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const cleaned = candidates
        .filter(Boolean)
        .map((c) => String(c).trim().toLowerCase())
        .filter((c) => c.length > 0);
    if (cleaned.length === 0) return null;

    const normalise = (s) =>
        String(s || '')
            .trim()
            .toLowerCase()
            .replace(/[_\s]+/g, ' '); // collapse underscores + repeated spaces

    for (const row of rows) {
        const haystack = normalise(row.name != null ? row.name : row.year);
        if (!haystack) continue;
        for (const cand of cleaned) {
            const needle = normalise(cand);
            if (haystack === needle) return row;
        }
    }

    // Fallback: substring containment (catches "VLSFO" vs "VLSFO (Very
    // Low Sulfur Fuel Oil)" and similar). Strict-equality pass above
    // wins when both shapes exist.
    for (const row of rows) {
        const haystack = normalise(row.name != null ? row.name : row.year);
        if (!haystack) continue;
        for (const cand of cleaned) {
            const needle = normalise(cand);
            if (haystack.includes(needle) || needle.includes(haystack)) {
                return row;
            }
        }
    }
    return null;
};

/**
 * Resolve a variant's ``spec_attributes`` JSON into the four deal-side
 * fuel-spec FK ids. Pure function -- the caller decides how to apply
 * the result to form state.
 *
 * Returns:
 *   {
 *     fuel_type:     <id|null>,
 *     fuel_grade:    <id|null>,
 *     sulfur_limit:  <id|null>,
 *     iso_8217_year: <id|null>,
 *     unmatched:     ['fuel_type', ...],   // slugs we couldn't resolve
 *   }
 *
 * ``lookups`` is the same object the rest of the form already uses,
 * shaped ``{fuelTypes, fuelGrades, sulfurLimits, iso8217Years}``.
 */
export const autofillSpecFromVariant = (specAttributes, lookups) => {
    const result = {
        fuel_type: null,
        fuel_grade: null,
        sulfur_limit: null,
        iso_8217_year: null,
        unmatched: [],
    };
    if (!specAttributes || typeof specAttributes !== 'object' || !lookups) {
        return result;
    }

    // --- fuel_type ---------------------------------------------------
    // Slug 'vlsfo' -> label 'VLSFO' (FUEL_CATEGORIES) -> shortcode
    // 'VLSFO'. We also feed the raw slug + the human label as
    // candidates so the matcher tolerates drift between the catalogue
    // and the lookup table.
    if (specAttributes.fuel_category) {
        const slug = specAttributes.fuel_category;
        const cat = FUEL_CATEGORIES.find((c) => c.value === slug);
        const label = getMixedFuelsLabel('fuel_category', slug);
        const candidates = [slug, label];
        if (cat) {
            candidates.push(cat.shortcode);
            // Strip parenthetical disambiguators so "Marine Gas Oil
            // (MGO)" matches a lookup named "MGO".
            const bareLabel = (cat.label || '').replace(/\s*\(.*\)\s*$/, '').trim();
            if (bareLabel) candidates.push(bareLabel);
        }
        const row = _findLookupByCandidates(lookups.fuelTypes, candidates);
        if (row) {
            result.fuel_type = row.id;
        } else {
            result.unmatched.push('fuel_type');
        }
    }

    // --- fuel_grade --------------------------------------------------
    // Slug 'rmg_380' -> label 'RMG 380'. Lookup table typically stores
    // 'RMG 380' or 'RMG_380'; the matcher normalises both.
    if (specAttributes.iso_grade) {
        const slug = specAttributes.iso_grade;
        const label = getMixedFuelsLabel('iso_grade', slug);
        const candidates = [slug, label, slug.replace(/_/g, ' ')];
        const row = _findLookupByCandidates(lookups.fuelGrades, candidates);
        if (row) {
            result.fuel_grade = row.id;
        } else {
            result.unmatched.push('fuel_grade');
        }
    }

    // --- sulfur_limit -----------------------------------------------
    // Slug 'max_0_50' -> label 'Max. 0.50%'. We also generate a few
    // common alternate shapes ('0.50%', '0.50%S') because the lookup
    // table's stored format isn't perfectly uniform.
    if (specAttributes.sulfur_limit) {
        const slug = specAttributes.sulfur_limit;
        const label = getMixedFuelsLabel('sulfur_limit', slug);
        const candidates = [slug, label];
        const slugMatch = slug.match(/^max_(\d+)_(\d+)$/);
        if (slugMatch) {
            const compact = `${slugMatch[1]}.${slugMatch[2]}%`;
            candidates.push(compact, `${compact}S`, `Max ${compact}`);
        }
        const row = _findLookupByCandidates(lookups.sulfurLimits, candidates);
        if (row) {
            result.sulfur_limit = row.id;
        } else {
            result.unmatched.push('sulfur_limit');
        }
    }

    // --- iso_8217_year -----------------------------------------------
    // Slug '2010' -> lookup row {year: 2010}. Match by string OR
    // numeric equality.
    if (specAttributes.iso_year) {
        const slug = String(specAttributes.iso_year);
        const row = _findLookupByCandidates(lookups.iso8217Years, [slug]);
        if (row) {
            result.iso_8217_year = row.id;
        } else {
            result.unmatched.push('iso_8217_year');
        }
    }

    return result;
};
